import { EntityCommand, EntityCommandPropertyValues, EntityCommandTypeEnum, IEntityCommand } from "@seriously-rad/seriouslyrad-framework-core/EntityCommand";
import { EntityCommandInformation } from "@seriously-rad/seriouslyrad-framework-core/EntityCommandInformation";
import { ComparisonExpression } from "@seriously-rad/seriouslyrad-framework-core/Expressions/ComparisonExpression";
import { PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { ObjectHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ObjectHelper";
import { Property } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { PermissionEvaluator } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/PermissionEvaluator";
import { WebApiClientService } from "@seriously-rad/seriouslyrad-framework-core/Server/WebApiClientService";
import { ServiceManager } from "@seriously-rad/seriouslyrad-framework-core/ServiceManager";
import { Dialog } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Dialog";
import { MessageDialog } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/MessageDialog";
import { DialogResultEnum } from "@seriously-rad/seriouslyrad-framework-core/Ui/Dialogs/DialogResultEnum";
import { ReportDownloadDialog } from "../Ui/Dialogs/ReportDownloadDialog";
import { ReportFilterDialog } from "../Ui/Dialogs/ReportFilterDialog";
import { NAMESPACE } from "./_Namespace";

export type EntityCommandReportRunReportPropertyValues =
    EntityCommandPropertyValues
    & {
    };

export type EntityCommandReportRunReportProperties = {
    [name in keyof EntityCommandReportRunReportPropertyValues]: Property<EntityCommandReportRunReportPropertyValues[name]>
};

export interface IEntityCommandReportRunReport extends IEntityCommand, EntityCommandReportRunReportProperties
{
}

export class EntityCommandReportRunReport extends EntityCommand implements IEntityCommandReportRunReport
{
    // #region Static

    public static override classNamespace = NAMESPACE + "EntityCommandReportRunReport";
    public static override className = "EntityCommandReportRunReport";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<EntityCommandReportRunReportPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                description: "Run Report",
                entityName: "Report",
                isDefault: true,
                key: "RUN_REPORT",
                permission: PermissionEvaluator.permissionLevels.execute,
                type: EntityCommandTypeEnum.entity,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Constructor

    constructor(propertyValues?: PartialNullable<EntityCommandReportRunReportPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Protected Methods

    protected _runReport(reportId: string, reportParameters: any, reportDownloadDialog: Dialog)
    {
        ServiceManager.getRequired(WebApiClientService)
            .createApiMethodRequest("DataService", "CallDataService", { id: "5912537e-4a2f-4dc0-a0a4-ec4159a7f8d3", operation: "RunReport", data: ObjectHelper.toJson({ reportId: reportId, reportParameters: reportParameters }) })
            .executeAsync()
            .then(data =>
            {
                if (data.scheduled)
                    MessageDialog.displayMessage("Report submitted", "Your report has been submitted. A notification will be sent when the report is available.");
                else
                    reportDownloadDialog.openAsync(data.userReportId);
            });
    }

    // #endregion

    // #region Overrides

    protected override _executeAsync(entityCommandInformation: EntityCommandInformation, value?: any)
    {
        return (
            super._executeAsync(entityCommandInformation, value)
                .then(() =>
                {
                    const
                        recordId = entityCommandInformation.getRecordId();

                    if (!recordId)
                        return;

                    const
                        caller = entityCommandInformation.caller.get();

                    if (!caller)
                        return;

                    if (!caller.reportFilterDialog)
                        caller.reportFilterDialog = new ReportFilterDialog();

                    if (!caller.reportDownloadDialog)
                        caller.reportDownloadDialog = new ReportDownloadDialog();

                    return (
                        ServiceManager.getRequired(WebApiClientService)
                            .createEntitySearchRequest(
                                "Reports",
                                {
                                    columns: ["reportFilterId"],
                                    filter: new ComparisonExpression({ path: "id", value: recordId }),
                                }
                            )
                            .executeAsync()
                            .then(data =>
                            {
                                if (data?.length && data[0].reportFilterId)
                                {
                                    (caller.reportFilterDialog as ReportFilterDialog)
                                        .openAsync(data[0].reportFilterId)
                                        .then(data =>
                                        {
                                            if (data.dialogResult !== DialogResultEnum.ok)
                                                return;

                                            this._runReport(recordId, caller.reportFilterDialog.createParameters(), caller.reportDownloadDialog);
                                        });
                                }
                                else
                                    this._runReport(recordId, null, caller.reportDownloadDialog);
                            })
                    );
                })
        );
    }

    // #endregion

    // #endregion
}

